import Container from '@ui/container/container';
import Text from '@ui/text/text/text';
import SelectSingle from '@ui/select/react-select/select-single';
import SlideDownCard from '@ui/cards/slide-down-card/slide-down-card';
import { ICONS } from '@ui/icons/types';
import ColorPill from './color-pill';
import NumberField from './number-field';

const BORDER_STYLE_OPTIONS = [
  { label: 'None', value: 'none' },
  { label: 'Solid', value: 'solid' },
  { label: 'Dashed', value: 'dashed' },
  { label: 'Dotted', value: 'dotted' },
];

interface BorderControlProps {
  style: Record<string, unknown>;
  onUpdateStyle: (key: string, value: unknown) => void;
  resolvedColor?: string;
  resolvedWidth?: number;
  defaultStyleValue?: string;
}

export default function BorderControl({
  style,
  onUpdateStyle,
  resolvedColor,
  resolvedWidth,
  defaultStyleValue = 'none',
}: BorderControlProps) {
  const borderStyleValue = (style.borderStyle as string | undefined) ?? defaultStyleValue;
  const isNone = borderStyleValue === 'none';
  const color = (style.borderColor as string | undefined) ?? resolvedColor ?? '#000000';
  const weight = style.border !== undefined ? parseInt(style.border as string, 10) : (resolvedWidth ?? 1);

  const handleChangeStyle = (value: string) => {
    onUpdateStyle('borderStyle', value);
    if (value === 'none') {
      onUpdateStyle('border', undefined);
    } else {
      if (style.border === undefined) onUpdateStyle('border', '1px');
      if (style.borderColor === undefined) onUpdateStyle('borderColor', resolvedColor ?? '#000000');
    }
  };

  return (
    <Container className="flex-column gap-12">
      <Container className="flex-column gap-2">
        <Text tag="label" design="GlobalIcoText3" className="L2 padding-l-16">
          Border
        </Text>
        <SelectSingle
          value={borderStyleValue}
          options={BORDER_STYLE_OPTIONS}
          placeholder="None"
          width="100%"
          menuPortal={false}
          onChange={handleChangeStyle}
        />
      </Container>

      <SlideDownCard open={!isNone} autoGrow animateOnInitialLoad={false}>
        <Container className="flex gap-8 ai-flex-end" padding="0 0 4px 0">
          <ColorPill
            label="Border color"
            value={color}
            isOverridden={style.borderColor !== undefined}
            onChange={(v) => onUpdateStyle('borderColor', v)}
            onReset={() => onUpdateStyle('borderColor', undefined)}
          />
          <Container className="flex-1">
            <NumberField
              label="Weight"
              allowNegative={false}
              min={1}
              max={12}
              icon={ICONS.BORDER_WEIGHT}
              value={weight}
              onChange={(v) => onUpdateStyle('border', v === undefined ? undefined : `${v}px`)}
            />
          </Container>
        </Container>
      </SlideDownCard>
    </Container>
  );
}
